import React, { FC, useEffect, useState } from 'react';

import { Box, HStack, Stack, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { customTheme } from 'theme';

import { CopyToClipboardIconButton, PrimaryButton } from 'components/DataEntry';
import { DisplayField } from 'components/DataEntry/OutlinedForm';
import {
  ExclamationTriangleIcon,
  InformationAlert,
  KeyIcon,
  OutlinedForm,
  PageHeaderWithIcon,
} from 'components/index';
import { Modal } from 'components/Overlay';
import { selectApp } from 'containers/App/selectors';

import { selectApiKeys } from '../selectors';
import { actions } from '../slice';

interface ICreateApiKeyModal {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const usageSnippet = (secret: string, org_name) =>
  `curl -X POST https://${org_name}.ariksa.io/v1/auth/token \\
  -H "Content-Type: application/json" \\
  -d '{"secret": "${secret}"}'`;

export const CreateApiKeyModal: FC<ICreateApiKeyModal> = props => {
  const { isOpen, onClose, onCreated } = props;
  const dispatch = useDispatch();
  const { createApiKey } = useSelector(selectApiKeys);
  const [name, setName] = useState('');
  const [expiresAt, setExpiresAt] = useState<Date | undefined>(undefined);
  const { user } = useSelector(selectApp);

  const created = createApiKey.data;
  const isCreating = createApiKey.loading;

  useEffect(() => {
    if (isOpen) {
      dispatch(actions.resetCreateApiKey());
    }
  }, [dispatch, isOpen]);

  const reset = () => {
    setName('');
    setExpiresAt(undefined);
    dispatch(actions.resetCreateApiKey());
  };

  const handleClose = () => {
    const hadCreated = !!created;
    reset();
    onClose();
    if (hadCreated) {
      onCreated();
    }
  };

  const handleSubmit = data => {
    dispatch(
      actions.createApiKey({
        name: data.name,

        expiration_time: expiresAt ? expiresAt.toISOString() : undefined,
      }),
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={created ? () => {} : handleClose}
      closeButton={!created}
      closeOnOverlayClick={!created}
      closeOnEsc={!created}
      header={
        <Box pl={3}>
          <PageHeaderWithIcon
            label={(!created ? 'Create' : '') + ' API Key'}
            icon={<KeyIcon />}
          />
        </Box>
      }
      styles={{
        modal: { size: created ? '3xl' : 'xl' },
        body: { pr: 4 },
      }}
      footer={
        created ? (
          <HStack w="full" justify="flex-end">
            <PrimaryButton onClick={handleClose}>Done</PrimaryButton>
          </HStack>
        ) : undefined
      }
      body={
        created ? (
          <Stack spacing={5} pl={2}>
            <Box pr={8}>
              <InformationAlert
                icon={
                  <ExclamationTriangleIcon
                    color={customTheme.colors.red['400']}
                  />
                }
                description="Please save the token somewhere safe and accessible. For security
                reasons, you won’t be able to view it again from Ariksa. If you lose
                this token, you’ll need to generate another token"
                bg="#fdf8e9"
                border="1px solid"
                borderColor={customTheme.colors.componentBorder}
                alignItems="flex-start"
              />
            </Box>
            <OutlinedForm
              fields={[
                {
                  type: 'custom',
                  name: 'api-key',
                  //label: 'API Key',
                  component: (
                    <HStack w="full" align="center" spacing={0}>
                      <DisplayField
                        field={{
                          label: 'API Key',
                          value:
                            'ak_564cf6ec80ab_y3pgTRap_817eZ98guyeO9mo0Cb8VmbM84mLHPZxLNg', //created?.secret,
                          type: 'display',
                          name: 'api-key-display',
                          styles: {
                            field: { w: 'full', flex: 1 },
                          },
                        }}
                      />
                      {/*<Box flex={1}>{created.secret}</Box>*/}
                      <CopyToClipboardIconButton
                        tooltip="Copy API key"
                        value={created?.secret}
                        flexShrink={0}
                      />
                    </HStack>
                  ),
                },
                {
                  type: 'display',
                  name: 'using-this-key',
                  label: 'Using this key',
                  value: (
                    <Stack w="full" spacing={2}>
                      <Text fontSize="sm">
                        Exchange this API key for a short-lived access token.
                        That will work as an access token
                      </Text>
                      <Box
                        as="pre"
                        fontSize="12px"
                        fontFamily="mono"
                        bg={customTheme.colors.gray[50]}
                        borderRadius={4}
                        p={3}
                        overflowX="auto"
                        whiteSpace="pre-wrap"
                        wordBreak="break-all"
                      >
                        {usageSnippet(created?.secret, user.info.org_id)}
                      </Box>
                    </Stack>
                  ),
                  styles: { field: { h: 'auto', pb: 4, mr: 8 } },
                },
              ]}
            />
          </Stack>
        ) : (
          <Box pr={8} pl={2}>
            <OutlinedForm
              fields={[
                {
                  type: 'text',
                  name: 'name',
                  label: 'Name',
                  value: name,
                  onChange: event => setName(event.target.value),
                  inputProps: {
                    autoFocus: true,
                    isRequired: true,
                    title: 'A label to help you identify this key later',
                  },
                },
                {
                  type: 'dateRange',
                  name: 'expires_at',
                  label: 'Expiration date (optional)',
                  dateProps: {
                    mode: 'single',
                    fromDate: dayjs().add(1, 'day').toDate(),
                    selected: expiresAt,
                    onSelect: selected => setExpiresAt(selected),
                  },
                },
                {
                  type: 'custom',
                  name: 'api-key-information',
                  component: (
                    <InformationAlert
                      icon={
                        <ExclamationTriangleIcon
                          color={customTheme.colors.red['400']}
                        />
                      }
                      description="Once the key is generated, please copy for use. It will be shown only once after creation"
                      bg="#fdf8e9"
                      border="1px solid"
                      borderColor={customTheme.colors.componentBorder}
                    />
                  ),
                },
              ]}
              onCancel={handleClose}
              onSubmit={event => {
                event.preventDefault();
                handleSubmit({ name });
              }}
              submitButton={{
                label: 'CREATE',
                isLoading: isCreating,
                isDisabled: !name,
              }}
            />
          </Box>
        )
      }
    />
  );
};
